import merge from 'deepmerge';
import { httpsCallable } from 'firebase/functions';
import { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Language } from '../common/CountryMarketConfig';
import {
  Ingredient,
  LocalizableProperty,
  RawIngredient,
  RawIngredientMarketVariant,
} from '../common/Ingredients/types';
import { useCountryMarketState } from '../controllers/CountryMarketController/CountryMarketState';
import { useIngredientsState } from '../features/Briefing/IngredientsState';
import { functions } from '../firebase';
import { useAuth } from './useAuth';
import {
  CountryMarketHelpers,
  useCountryMarketHelpers,
} from './useCountryMarketHelpers';

interface IngredientsHandler {
  loadIngredientsForCountryMarket(
    countryAbbreviation: string,
    marketAbbreviation: string,
    countryMarket: string,
    marketId: number,
    language: string,
  ): void;
}

/**
 *
 * @param localizedArray An array of {locale, data} items
 * @param userLocale e.g. en_us
 * @returns The data prop of the first localizedArray item that matches the userLocale, or as a fallback the first item's data prop
 */
export const getLocalizedString = (
  localizedArray: LocalizableProperty[] | undefined,
  userLocale: string,
  firstItemFallback?: boolean,
): string | undefined => {
  userLocale = userLocale.toLowerCase();

  if (!localizedArray) return;

  let itemName = localizedArray.find(({ locale }) => {
    return locale === userLocale;
  });

  if (!itemName && firstItemFallback) {
    itemName = localizedArray[0];
  }

  return itemName?.data;
};

/**
 *
 * @param ingredient The ingredient item
 * @param marketId e.g. PC
 * @returns The data for the ingredient that matches the marketId
 */
export const getIngredientMarketVariant = (
  ingredient: RawIngredient,
  marketId: string,
): RawIngredientMarketVariant | undefined => {
  return ingredient.marketVariants.find(
    (variant) => variant.marketId === marketId,
  );
};

/**
 *
 * @param item The ingredient object
 * @param country e.g. GBR
 * @param marketSegment e.g. PC
 * @returns MarketingClaims for the country (if there are any), and MarketingClaims under ALL if there isn't
 */
export const getItemMarketingClaims = (
  item: RawIngredient,
  country: string,
  marketSegment: string,
): string[] | undefined => {
  let marketingClaims:
    | {
        subCategoryId: string;
      }[]
    | undefined;
  // Legacy code temporarily in place for AZEELAB-304
  if (item.countries) {
    const result =
      item.countries.find((itemCountry) => itemCountry.countryId === country) ??
      item.countries.find((itemCountry) => itemCountry.countryId === 'ALL');

    marketingClaims = result?.marketingClaims;
  } else {
    ////////
    // TODO AZEELAB-304: Once it's confirmed that the new data structure is present on live,
    // remove the above code (the if check and everything using the result variable), and
    // then move the subsequent find of marketingClaims into getLocalizedData, since that
    // uses getIngredientMarketVariant. You can then delete this function.

    const itemMarketVariant = getIngredientMarketVariant(item, marketSegment);
    marketingClaims = itemMarketVariant?.marketingClaims;
  }
  return marketingClaims?.map(({ subCategoryId }) => subCategoryId);
};

/**
 *
 * @param item The ingredient item
 * @param userLocale e.g. en_us (Should be lowercased in ContentController)
 * @param marketSegment e.g. PC
 * @returns The name, description, and reference data for the ingredient
 */
export const getLocalizedData = (
  item: RawIngredient,
  userLocale: string,
  marketSegment: string,
  t: TFunction,
): {
  name: string;
  reference: string | undefined;
  description: string | undefined;
} => {
  userLocale = userLocale.toLowerCase();

  // We only want to calculate this once, so we're packing all data-finding operations that depend
  // on this into this single function.
  const itemMarketVariant = getIngredientMarketVariant(item, marketSegment);

  // Item name includes some fallbacks
  const name: string =
    // Initially, we check for PortalDisplayName in correct language
    getLocalizedString(itemMarketVariant?.portalDisplayName, userLocale) ||
    // We fallback to PortalDisplayName in fallback language (default: en_US)
    getLocalizedString(itemMarketVariant?.portalDisplayName, 'en_us') ||
    // We fallback to ProductName in correct language
    getLocalizedString(item.productName, userLocale) ||
    // We fallback to ProductName in fallback language (default: en_US)
    getLocalizedString(item.productName, 'en_us') ||
    // We fallback to the localized Untitled string
    t('labels:untitled');

  // TODO AZEELAB-304: Once it's confirmed that the new data structure is present on live,
  // remove the getLocalizedString call here and just use the string value
  const reference = Array.isArray(itemMarketVariant?.inciName)
    ? getLocalizedString(itemMarketVariant?.inciName, userLocale, true)
    : (itemMarketVariant?.inciName as string);

  const description = getLocalizedString(
    itemMarketVariant?.productDescription,
    userLocale,
    true,
  );

  // TODO AZEELAB-304: Add this in once the new structure is live
  // const marketingClaims = itemMarketVariant?.marketingClaims?.map(({ subCategoryId }) => subCategoryId)

  return { name, reference, description }; // marketingClaims}
};

export const getIngredients = async (
  countryAbbreviation: string,
  marketAbbreviation: string,
  marketIdNumber: number,
  userLocale: string,
  t: TFunction,
  {
    findCountryMarketConfigByCountryAndMarketAbbreviations,
  }: CountryMarketHelpers,
): Promise<Ingredient[]> =>
  httpsCallable(
    functions,
    'getIngredients',
  )({
    country: countryAbbreviation,
    marketSegment: marketAbbreviation,
  }).then(({ data }): Ingredient[] => {
    const { languages = [Language.enUS] } =
      findCountryMarketConfigByCountryAndMarketAbbreviations(
        countryAbbreviation,
        marketAbbreviation,
      ) ?? {};

    return (data as RawIngredient[]).map((item: RawIngredient): Ingredient => {
      // TODO AZEELAB-304: Once it's confirmed that the new data structure is present on live,
      // add marketingClaims to the return of this function, and use it as the value for marketing_claims
      // in the return below
      const {
        name,
        reference,
        description = '',
      } = getLocalizedData(item, userLocale, marketAbbreviation, t);
      // TODO AZEELAB-304: Once the new structure is live, add marketingClaims to the return above,
      // and remove this marketing_claims variable. Use marketingClaims in the final return below.
      const marketing_claims =
        getItemMarketingClaims(item, countryAbbreviation, marketAbbreviation) ??
        [];
      const applications = item.productApplications.map(
        ({ subCategoryId }) => subCategoryId,
      );

      return {
        itemNumber: item.itemNumber,
        name,
        reference,
        description,
        applications,
        marketing_claims,
        manufacturer: item.manufacturer,
        // image_url: Could be itemMarketVariant?.productPictures, but the string is just e.g. "GettyImages_175408342", no file extension
        // On CP, the image urls are like "https://azelis-sta.forge.smile.fr/media/cache/attachment/filter/product_images_desktop/7d63fa4abdf748c0ad381313e878ea34/147/5fbcf96e12eda667491672.jpg"
        image_url: '',
        ingredient_url: languages
          .map((language) => ({
            [language]: `${process.env.REACT_APP_CP_BASEURL}/${language}/${marketIdNumber}/${item.itemNumber}`,
          }))
          .reduce((prev, actual) => merge(prev, actual), {}),
      };
    });
  });

export const useIngredientsHandler: () => IngredientsHandler = () => {
  const [, { setIngredients, setIngredientsError }] = useIngredientsState();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [{ user }] = useAuth();
  const [ingredientsState] = useIngredientsState();
  const { i18n } = useTranslation();
  const [{ countryMarketConfig }] = useCountryMarketState();

  const countryMarketHelpers = useCountryMarketHelpers();
  const { isCountryMarketConfigValidForString } = countryMarketHelpers;

  /**
   * The time (in *milliseconds*) after which the Ingredients in the Redux Store expire
   *
   * currently 3 days
   */
  //                                 ms   * s  * m  * h  * d
  const INGREDIENTS_CACHE_LIFETIME = 1000 * 60 * 60 * 24 * 3;
  /**
   * The time (in *milliseconds*) after which the Ingredients Error in the Redux Store expires
   *
   * currently 2 minutes
   */
  const INGREDIENTS_ERROR_LIFETIME = 1000 * 60 * 2;

  const loadIngredientsForCountryMarket = useCallback(
    (
      countryAbbreviation: string,
      marketAbbreviation: string,
      countryMarket: string,
      marketId: number,
      language: string,
    ) =>
      getIngredients(
        countryAbbreviation,
        marketAbbreviation,
        marketId,
        language,
        t,
        countryMarketHelpers,
      )
        .then((ingredients) => {
          dispatch(
            setIngredients({
              ingredients,
              countryMarket: countryMarket,
            }),
          );
        })
        .catch((error) => {
          dispatch(
            setIngredientsError({
              error: `Error from Server: ${error.code}`,
              countryMarket: countryMarket,
            }),
          );
        }),
    [countryMarketHelpers, dispatch, setIngredients, setIngredientsError, t],
  );

  const evaluateIngredients = useCallback(() => {
    if (!countryMarketConfig || !user) return;

    const countryMarket = countryMarketConfig.customerPortalMarketSegmentId;
    const state = ingredientsState[countryMarket];
    const ingredients = state?.ingredients;
    const error = state?.error;

    let updatedAtAsDate;
    if (state?.updatedAt) {
      try {
        updatedAtAsDate = new Date(state?.updatedAt);
      } catch (e) {
        updatedAtAsDate = undefined;
      }
    }

    const ingredientsAvailable = !!ingredients && ingredients.length > 0;
    const ingredientsAreOutdated =
      !updatedAtAsDate ||
      new Date().getTime() - updatedAtAsDate.getTime() >
        INGREDIENTS_CACHE_LIFETIME;
    const ingredientsAreValid = ingredientsAvailable && !ingredientsAreOutdated;

    const isInErrorRetention =
      !!error &&
      new Date().getTime() - (updatedAtAsDate || new Date(0)).getTime() <
        INGREDIENTS_ERROR_LIFETIME;

    if (ingredientsAreValid || isInErrorRetention) return;

    const marketIdNumber = Object.entries(user.countries_and_markets || {})
      .filter(([, value]) =>
        isCountryMarketConfigValidForString(countryMarketConfig, value),
      )
      .map(([key]) => key)[0];

    if (!marketIdNumber) return;

    loadIngredientsForCountryMarket(
      countryMarketConfig.datalakeCountryAbbreviation, // e.g. GBR
      countryMarketConfig.taxonomyMarketAbbreviaton, // e.g. PC
      countryMarket,
      parseInt(marketIdNumber),
      i18n.language,
    );
  }, [
    INGREDIENTS_CACHE_LIFETIME,
    INGREDIENTS_ERROR_LIFETIME,
    countryMarketConfig,
    i18n.language,
    ingredientsState,
    isCountryMarketConfigValidForString,
    loadIngredientsForCountryMarket,
    user,
  ]);

  useEffect(() => {
    evaluateIngredients();

    const evaluationInterval = setInterval(
      evaluateIngredients,
      Math.min(INGREDIENTS_CACHE_LIFETIME, INGREDIENTS_ERROR_LIFETIME) + 1000,
    );

    return () => clearInterval(evaluationInterval);
  }, [
    INGREDIENTS_CACHE_LIFETIME,
    INGREDIENTS_ERROR_LIFETIME,
    evaluateIngredients,
  ]);

  return useMemo(
    () => ({
      loadIngredientsForCountryMarket,
    }),
    [loadIngredientsForCountryMarket],
  );
};
